import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { BORDER_RADIUS, COLORS } from "utils/styles";

const Container = styled.div`
  /* Positioning */
  position: absolute;
  bottom: 5rem;
  left: 30vw;

  max-width: 60vw;
  transform: translateX(-50%);
  width: 20rem;
  z-index: 10;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  user-select: none;
  -moz-user-select: none;
`;

const Labels = styled.div.attrs({ className: "pb-2" })`
  display: flex;
`;

const Label = styled.div`
  font-size: 0.75rem;
  flex-grow: ${({ size }) => size};
  text-align: left;
`;

const ScrollContainer = styled.div`
  ${BORDER_RADIUS}
  background-color: ${COLORS.WHITE};
  height: 1rem;
  position: relative;
  padding: 0.125rem;
`;

const Scroller = styled.div`
  ${BORDER_RADIUS}
  height: 0.75rem;
  background-color: ${COLORS.BLACK};
  width: ${({ width }) => width}%;
  position: absolute;
  left: ${({ position }) => position}px;
  transition: width 300ms;
`;

export default function ScrollControll({
  labels,
  onScroll,
  targetProgress,
  scrollerWidth,
}) {
  const scrollContainerRef = useRef(null);
  const scrollerRef = useRef(null);

  const [position, setPosition] = useState();
  const [windowResizedAt, setWindowResizedAt] = useState(Date.now());

  const draggingRef = useRef(false);
  const dragOffsetRef = useRef(0);

  const containerPadding = () => {
    if (typeof window === "undefined" || !scrollContainerRef.current)
      return [0, 0];

    const { paddingLeft, paddingRight } = window.getComputedStyle(
      scrollContainerRef.current
    );

    return [parseInt(paddingLeft), parseInt(paddingRight)];
  };

  const scrollerBoundingRect = () =>
    scrollerRef.current ? scrollerRef.current.getBoundingClientRect() : {};

  const scrollContainerBoundingRect = () =>
    scrollContainerRef.current
      ? scrollContainerRef.current.getBoundingClientRect()
      : {};

  const maxScrollingPixels = () => {
    const { width: scrollContainerWidth } = scrollContainerBoundingRect();
    const { width: scrollerWidth } = scrollerBoundingRect();
    const [containerPaddingLeft, containerPaddingRight] = containerPadding();
    return (
      scrollContainerWidth -
      scrollerWidth -
      containerPaddingLeft -
      containerPaddingRight
    );
  };

  const progress = useMemo(() => {
    const [containerPaddingLeft, containerPaddingRight] = containerPadding();
    return (position - containerPaddingLeft) / maxScrollingPixels();
  }, [position]);

  const handleMouseMove = (event) => {
    if (!draggingRef.current) return false;
    event.stopPropagation() && event.preventDefault();

    const {
      x: containerX,
      width: containerWidth,
    } = scrollContainerBoundingRect();

    let newPosition;
    const [containerPaddingLeft, containerPaddingRight] = containerPadding();

    if (event.clientX < containerX) {
      newPosition = containerPaddingLeft;
    } else if (event.clientX > containerX + containerWidth) {
      newPosition = maxScrollingPixels();
    } else {
      newPosition = Math.max(
        Math.min(
          event.clientX -
            dragOffsetRef.current -
            containerX +
            containerPaddingLeft,
          maxScrollingPixels() + containerPaddingLeft
        ),
        containerPaddingLeft
      );
    }

    setPosition(newPosition);
  };

  const handleDragStart = (event) => {
    if (
      ![scrollerRef.current, scrollContainerRef.current].includes(event.target)
    )
      return;

    event.stopPropagation() && event.preventDefault();

    event.returnValue = false;

    const { width: scrollerRefWidth, x: scrollerRefX } = scrollerBoundingRect();

    if (event.target == scrollerRef.current)
      dragOffsetRef.current = event.clientX - scrollerRefX;
    else if (event.target == scrollContainerRef.current)
      dragOffsetRef.current = scrollerRefWidth / 2;

    draggingRef.current = true;
  };

  const handleDragEnd = (event) =>
    draggingRef.current && (draggingRef.current = false);

  const handleWindowResize = (event) => setWindowResizedAt(Date.now());

  const setProgress = (progress) => {
    const [containerPaddingLeft, containerPaddingRight] = containerPadding();
    setPosition(
      Math.max(
        0,
        Math.min(maxScrollingPixels(), progress * maxScrollingPixels())
      ) + containerPaddingLeft
    );
  };

  useEffect(() => {
    window.addEventListener("mousedown", handleDragStart);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleWindowResize);

    return function cleanup() {
      window.removeEventListener("mousedown", handleDragStart);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    draggingRef.current && onScroll && onScroll(progress);
  }, [progress]);

  useEffect(() => {
    if (!draggingRef.current) setProgress(targetProgress);
  }, [targetProgress]);

  return (
    <Container>
      <Labels>
        {labels.map(({ year, size }) => (
          <Label key={year} size={size}>
            {year}
          </Label>
        ))}
      </Labels>
      <ScrollContainer ref={scrollContainerRef}>
        <Scroller
          ref={scrollerRef}
          segmentLength={labels.length}
          position={position}
          width={scrollerWidth}
        />
      </ScrollContainer>
    </Container>
  );
}
